import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import {
  Package,
  Download,
  Eye,
  Filter,
  ChevronDown,
  ChevronUp,
  Calendar,
  CreditCard,
  Truck,
  ArrowLeft,
  FileText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import orderService, { Order, OrderFilters } from "@/services/orderService";
import { formatCurrency } from "@/lib/utils";

const OrderCard = ({ order }: { order: Order }) => {
  const [expanded, setExpanded] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleDownload = async (productId: string) => {
    try {
      const downloadInfo = await orderService.downloadDigitalProduct(
        order._id,
        productId
      );

      // En un entorno real, esto abriría la descarga
      toast({
        title: "Descarga iniciada",
        description: `Descargando ${downloadInfo.productTitle}...`,
      });

      // Simular descarga (en producción sería window.open(downloadInfo.downloadUrl))
      console.log("Download URL:", downloadInfo.downloadUrl);
    } catch (error) {
      toast({
        title: "Error en descarga",
        description: "No se pudo iniciar la descarga del producto",
        variant: "destructive",
      });
    }
  };

  const digitalItems = order.items.filter(
    (item) => item.product.type === "digital"
  );
  const physicalItems = order.items.filter(
    (item) => item.product.type === "físico"
  );

  const paidStatus = order.paymentInfo?.status ?? order.paymentStatus;
  const canDownloadReceipt =
    paidStatus === "completed" || paidStatus === "refunded";

  const handleDownloadReceipt = async () => {
    try {
      await orderService.downloadReceipt(order._id);
    } catch {
      toast({
        title: "No se pudo descargar el recibo",
        variant: "destructive",
      });
    }
  };

  return (
    <Card className="w-full">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <CardTitle className="text-lg">
              Orden #{order.orderNumber}
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              {orderService.formatOrderDate(order.createdAt)}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={orderService.getStatusColor(order.status)}>
              {orderService.getStatusLabel(order.status)}
            </Badge>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        <div className="flex items-center justify-between pt-2">
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-1">
              <Package className="h-4 w-4" />
              {order.totalItems}{" "}
              {order.totalItems === 1 ? "artículo" : "artículos"}
            </div>
            <div className="flex items-center gap-1">
              <CreditCard className="h-4 w-4" />
              {order.paymentMethod}
            </div>
          </div>
          <div className="text-right">
            <div className="font-bold text-lg">
              {formatCurrency(order.total)}
            </div>
          </div>
        </div>
      </CardHeader>

      {expanded && (
        <CardContent className="pt-0">
          <div className="space-y-4">
            {canDownloadReceipt && (
              <div className="border-t pt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadReceipt}
                >
                  <FileText className="h-4 w-4 mr-1" />
                  Descargar recibo
                </Button>
              </div>
            )}
            {/* Información de Envío */}
            <div className="border-t pt-4">
              <h4 className="font-medium flex items-center gap-2 mb-3">
                <Truck className="h-4 w-4" />
                Información de Envío
              </h4>
              <div className="bg-gray-50 rounded-lg p-3 text-sm">
                <p className="font-medium">{order.shippingAddress?.fullName}</p>
                <p>{order.shippingAddress?.street}</p>
                <p>
                  {order.shippingAddress?.district},{" "}
                  {order.shippingAddress?.postalCode}
                </p>
                <p>Tel: {order.shippingAddress?.phone}</p>
              </div>
            </div>

            {/* Productos Físicos */}
            {physicalItems.length > 0 && (
              <div className="border-t pt-4">
                <h4 className="font-medium mb-3">Productos Físicos</h4>
                <div className="space-y-3">
                  {physicalItems.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 p-3 border rounded-lg"
                    >
                      <img
                        src={item.product.image}
                        alt={item.product.title}
                        className="w-12 h-12 object-cover rounded cursor-pointer"
                        onClick={() =>
                          navigate(`/products/${item.product._id}`)
                        }
                      />
                      <div className="flex-1">
                        <h5
                          className="font-medium text-sm cursor-pointer hover:text-primary"
                          onClick={() =>
                            navigate(`/products/${item.product._id}`)
                          }
                        >
                          {item.product.title}
                        </h5>
                        <p className="text-xs text-muted-foreground">
                          Cantidad: {item.quantity} ×{" "}
                          {formatCurrency(item.price)}
                        </p>
                      </div>
                      <div className="text-sm font-medium">
                        {formatCurrency(item.price * item.quantity)}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          navigate(`/products/${item.product._id}`)
                        }
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Productos Digitales */}
            {digitalItems.length > 0 && (
              <div className="border-t pt-4">
                <h4 className="font-medium mb-3">Productos Digitales</h4>
                <div className="space-y-3">
                  {digitalItems.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 p-3 border rounded-lg bg-green-50"
                    >
                      <img
                        src={item.product.image}
                        alt={item.product.title}
                        className="w-12 h-12 object-cover rounded cursor-pointer"
                        onClick={() =>
                          navigate(`/products/${item.product._id}`)
                        }
                      />
                      <div className="flex-1">
                        <h5
                          className="font-medium text-sm cursor-pointer hover:text-primary"
                          onClick={() =>
                            navigate(`/products/${item.product._id}`)
                          }
                        >
                          {item.product.title}
                        </h5>
                        <p className="text-xs text-muted-foreground">
                          Descarga disponible
                        </p>
                      </div>
                      <div className="text-sm font-medium">
                        {formatCurrency(item.price)}
                      </div>
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => handleDownload(item.product._id)}
                        disabled={order.status !== "delivered"}
                      >
                        <Download className="h-4 w-4 mr-1" />
                        Descargar
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Resumen de Costos */}
            <div className="border-t pt-4">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Subtotal:</span>
                  <span>{formatCurrency(order.subtotal)}</span>
                </div>
                {order.shipping > 0 && (
                  <div className="flex justify-between">
                    <span>Envío:</span>
                    <span>{formatCurrency(order.shipping)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-base border-t pt-2">
                  <span>Total:</span>
                  <span>{formatCurrency(order.total)}</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

const OrdersContent = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [filters, setFilters] = useState<OrderFilters>({ page: 1, limit: 10 });
  const [showFilters, setShowFilters] = useState(false);

  const {
    data: ordersData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["orders", filters],
    queryFn: () => orderService.getMyOrders(filters),
    staleTime: 5 * 60 * 1000, // 5 minutos
  });

  const handleFilterChange = (
    key: keyof OrderFilters,
    value: string | number | undefined
  ) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: 1, // Reset a primera página cuando cambian filtros
    }));
  };

  const clearFilters = () => {
    setFilters({ page: 1, limit: 10 });
  };

  const handlePageChange = (newPage: number) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  if (error) {
    toast({
      title: "Error",
      description: "No se pudieron cargar las órdenes",
      variant: "destructive",
    });
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6">
        <Button variant="ghost" onClick={() => navigate("/")} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Volver al Inicio
        </Button>

        <h1 className="text-3xl font-playfair font-bold text-brown mb-2">
          Mis Órdenes
        </h1>
        <p className="text-muted-foreground">
          Historial completo de tus compras y descargas
        </p>
      </div>

      {/* Filtros */}
      <div className="mb-6">
        <Button
          variant="outline"
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-2"
        >
          <Filter className="h-4 w-4" />
          Filtros
        </Button>

        {showFilters && (
          <Card className="mt-4">
            <CardContent className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Estado
                  </label>
                  <select
                    className="w-full p-2 border rounded-md"
                    value={filters.status || ""}
                    onChange={(e) =>
                      handleFilterChange("status", e.target.value || undefined)
                    }
                  >
                    <option value="">Todos</option>
                    <option value="pending">Pendiente</option>
                    <option value="processing">Procesando</option>
                    <option value="shipped">Enviado</option>
                    <option value="delivered">Entregado</option>
                    <option value="cancelled">Cancelado</option>
                  </select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Fecha Desde
                  </label>
                  <Input
                    type="date"
                    value={filters.startDate || ""}
                    onChange={(e) =>
                      handleFilterChange(
                        "startDate",
                        e.target.value || undefined
                      )
                    }
                  />
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Fecha Hasta
                  </label>
                  <Input
                    type="date"
                    value={filters.endDate || ""}
                    onChange={(e) =>
                      handleFilterChange("endDate", e.target.value || undefined)
                    }
                  />
                </div>

                <div className="flex items-end">
                  <Button
                    variant="outline"
                    onClick={clearFilters}
                    className="w-full"
                  >
                    Limpiar Filtros
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Lista de Órdenes */}
      {isLoading ? (
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="space-y-2">
                    <Skeleton className="h-6 w-32" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                  <Skeleton className="h-6 w-20" />
                </div>
              </CardHeader>
            </Card>
          ))}
        </div>
      ) : ordersData?.orders.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No tienes órdenes</h3>
            <p className="text-muted-foreground mb-4">
              Cuando realices tu primera compra, aparecerá aquí
            </p>
            <Button onClick={() => navigate("/products")}>
              Explorar Productos
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="space-y-4">
            {ordersData?.orders.map((order) => (
              <OrderCard key={order._id} order={order} />
            ))}
          </div>

          {/* Paginación */}
          {ordersData?.pagination && ordersData.pagination.totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-8">
              <Button
                variant="outline"
                onClick={() => handlePageChange(filters.page! - 1)}
                disabled={!ordersData.pagination.hasPrevPage}
              >
                Anterior
              </Button>

              <span className="text-sm text-muted-foreground px-4">
                Página {ordersData.pagination.currentPage} de{" "}
                {ordersData.pagination.totalPages}
              </span>

              <Button
                variant="outline"
                onClick={() => handlePageChange(filters.page! + 1)}
                disabled={!ordersData.pagination.hasNextPage}
              >
                Siguiente
              </Button>
            </div>
          )}

          {/* Estadísticas */}
          {ordersData?.pagination && (
            <div className="text-center text-sm text-muted-foreground mt-4">
              Total: {ordersData.pagination.totalOrders}{" "}
              {ordersData.pagination.totalOrders === 1 ? "orden" : "órdenes"}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const Orders = () => {
  return (
    <ProtectedRoute>
      <OrdersContent />
    </ProtectedRoute>
  );
};

export default Orders;
